import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppState } from '@/contexts/AppStateContext';
import { FullscreenLoader } from '@/components/FullscreenLoader';

// ============================================================================
// ROUTE GUARD v4 - Trial-First Flow
// ============================================================================
// 
// NOUVEAU FLUX:
// 1. Signup/Login → /setup (configuration établissement) [OBLIGATOIRE]
// 2. /setup → /wow (écran stats + génération IA)
// 3. /wow → /dashboard (mode trial si pas de subscription)
// 4. /choose-plan proposé via bandeau soft dans le dashboard
//
// RÈGLES:
// - isAuthenticated: Session Supabase valide
// - isConfigured: au moins 1 establishment
// - hasActiveSubscription: subscription = 'active' | 'trial'
//   → SI false: dashboard accessible en mode trial (bandeau)
//
// ============================================================================

interface RouteGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  redirectIfAuthenticated?: boolean;
  skipSubscriptionCheck?: boolean;
  skipEmailVerification?: boolean;
}

// Routes exemptées de la redirection vers /setup
const SETUP_FLOW_ROUTES = ['/setup', '/setup-wizard', '/profile-discovery', '/wow'];

// Routes exemptées de la vérification email
const EMAIL_EXEMPT_ROUTES = ['/choose-plan', '/success', '/verify-email', '/auth-reconnect', '/wow'];

export const RouteGuard = ({
  children,
  requireAuth = false,
  redirectIfAuthenticated = false,
  skipSubscriptionCheck = false,
  skipEmailVerification = false,
}: RouteGuardProps) => {
  const {
    isAuthenticated,
    isInitializing,
    isLoadingProfile,
    isLoadingEstablishments,
    hasActiveSubscription,
    isConfigured,
    isEmailVerified,
    isGoogleUser,
    subscriptionStatus,
  } = useAppState();
  
  const location = useLocation();
  const currentPath = location.pathname;

  // ============================================================================
  // STATE 1: LOADING
  // ============================================================================
  
  if (isInitializing) {
    return <FullscreenLoader message="Chargement..." />;
  }

  if (requireAuth && isAuthenticated && isLoadingProfile) {
    return <FullscreenLoader message="Chargement du profil..." />;
  }

  // ============================================================================
  // STATE 2: UNAUTHENTICATED on protected route → /login
  // ============================================================================
  
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: currentPath }} replace />;
  }

  // ============================================================================
  // STATE 3: AUTHENTICATED on auth page → redirect away
  // ============================================================================
  
  if (redirectIfAuthenticated && isAuthenticated) {
    if (subscriptionStatus === null || isLoadingProfile || isLoadingEstablishments) {
      return <FullscreenLoader message="Chargement du profil..." />;
    }

    // Smart redirect: setup → dashboard (no paywall blocking)
    let destination = '/setup';
    
    if (isConfigured) {
      // Go directly to dashboard - trial mode handles upsell
      if (isEmailVerified || isGoogleUser) {
        destination = '/dashboard';
      } else {
        destination = '/verify-email';
      }
    }
    
    return <Navigate to={destination} replace />;
  }

  // ============================================================================
  // STATE 4: ESTABLISHMENT CHECK (setup required before anything)
  // ============================================================================
  
  if (requireAuth && isAuthenticated && !skipSubscriptionCheck) {
    const isSetupFlowRoute = SETUP_FLOW_ROUTES.some(route => currentPath.startsWith(route));
    const isPaymentFlowRoute = currentPath.startsWith('/choose-plan') || currentPath.startsWith('/success');
    const hasSessionId = location.search.includes('session_id=');

    if (subscriptionStatus === null) {
      return <FullscreenLoader message="Chargement du profil..." />;
    }

    // Redirect to /setup if no establishment configured
    if (!isConfigured && !isSetupFlowRoute && !isPaymentFlowRoute && !hasSessionId) {
      return <Navigate to="/setup" replace />;
    }
    
    // NO LONGER blocking access to dashboard for missing subscription
    // Trial mode banner handles the upsell in-app
  }

  // ============================================================================
  // STATE 5: EMAIL VERIFICATION CHECK
  // ============================================================================
  
  if (requireAuth && isAuthenticated && hasActiveSubscription && !skipEmailVerification) {
    const isEmailExemptRoute = EMAIL_EXEMPT_ROUTES.some(route => currentPath.startsWith(route));
    
    if (!isEmailVerified && !isGoogleUser && !isEmailExemptRoute) {
      return <Navigate to="/verify-email" replace />;
    }
  }

  // ============================================================================
  // STATE 6: ACCESS GRANTED
  // ============================================================================
  
  return <>{children}</>;
};
